import { getFileFromFirebaseStorage } from "../../../../api/firebase/firestoreService";

interface HandleDocumentSelectionParams {
  value: string;
  documentsList?: { value: string; label: string }[];
  defaultLabel?: string;
  latestFetchIdRef: React.MutableRefObject<number>;
  addFileFromStorage: (file: File, displayName?: string) => void;
  setDocumentTitle: (title: string) => void;
  setIsDocumentLoading: (loading: boolean) => void;
  setDocumentError: (error: string | null) => void;
}

/**
 * Handles document selection from the dropdown in the NAR letter modal.
 *
 * Fetches the selected document from Firebase Storage, then adds it to the
 * upload file list and sets the document title to the selected option's label.
 *
 * Implements a stale-request guard: each call increments a counter on
 * `latestFetchIdRef`. If another selection is made while a fetch is still
 * in-flight, the earlier fetch's result is silently discarded, preventing
 * race conditions where an outdated file overwrites the user's latest choice.
 *
 * @param params.value - The Firebase Storage path of the selected document.
 * @param params.documentsList - Available dropdown options with value/label pairs.
 * @param params.defaultLabel - Fallback label when the selected document or list label is unavailable.
 * @param params.latestFetchIdRef - Mutable ref tracking the most recent fetch to detect stale responses.
 * @param params.addFileFromStorage - Callback that replaces any prior storage-sourced file and appends the new one.
 * @param params.setDocumentTitle - Callback to update the document title input.
 * @param params.setIsDocumentLoading - Tracks the latest selection download.
 * @param params.setDocumentError - Shows a safe error message inside the modal.
 */
export const handleDocumentSelection = async ({
  value,
  documentsList,
  defaultLabel,
  latestFetchIdRef,
  addFileFromStorage,
  setDocumentTitle,
  setIsDocumentLoading,
  setDocumentError,
}: HandleDocumentSelectionParams) => {
  const fetchId = ++latestFetchIdRef.current;
  const startedAt = Date.now();
  let stage = "download";
  setIsDocumentLoading(true);
  setDocumentError(null);
  console.log("[documentSelection] started", {
    fetchId,
    online: navigator.onLine,
    hasValue: Boolean(value),
    optionCount: documentsList?.length ?? 0,
  });

  try {
    if (!value) throw new Error("No document selected");
    const fileToFetch = await getFileFromFirebaseStorage(value, fetchId);
    if (fetchId !== latestFetchIdRef.current) {
      console.log("[documentSelection] stale result discarded", { fetchId });
      return;
    }
    console.log("[documentSelection] downloaded", {
      fetchId,
      bytes: fileToFetch.size,
      elapsedMs: Date.now() - startedAt,
    });

    const selectedDoc = documentsList?.find((doc) => doc.value === value);
    const docLabel = selectedDoc?.label ?? defaultLabel ?? "Auth Letter";
    stage = "update-file-list";
    addFileFromStorage(fileToFetch, docLabel);
    stage = "update-title";
    setDocumentTitle(docLabel);
    console.log("[documentSelection] completed", {
      fetchId,
      matchedOption: Boolean(selectedDoc),
      elapsedMs: Date.now() - startedAt,
    });
  } catch (error) {
    console.log("[documentSelection] failed", {
      fetchId,
      stage,
      stale: fetchId !== latestFetchIdRef.current,
      elapsedMs: Date.now() - startedAt,
      errorName: error instanceof Error ? error.name : "UnknownError",
    });
    if (fetchId === latestFetchIdRef.current) {
      setDocumentError(
        "Could not load the selected document. Please select it again or upload the file manually.",
      );
    }
  } finally {
    // An older request must never clear the latest request's loading state.
    if (fetchId === latestFetchIdRef.current) {
      setIsDocumentLoading(false);
      console.log("[documentSelection] loading finished", { fetchId });
    }
  }
};
